/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths.
 *
 * Shopify customer forms reject fetch/XHR on many shops. Submit into a
 * hidden iframe so the footer does not reload.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;
    /** @type {HTMLFormElement | null} */
    this.form = null;
    /** @type {HTMLIFrameElement | null} */
    this.iframe = null;
    /** @type {HTMLElement | null} */
    this.successMessage = null;
    /** @type {HTMLElement | null} */
    this.errorMessage = null;
    /** @type {boolean} */
    this.pendingSubmit = false;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onIframeLoad = this.onIframeLoad.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.email-signup__button--integrated');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');
    this.form = this.querySelector('form');
    this.iframe = this.querySelector('.email-signup__iframe');
    this.successMessage = this.querySelector('.email-signup__message--success');
    this.errorMessage = this.querySelector('.email-signup__message--error');

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }

    if (this.form && this.iframe?.name) {
      this.form.setAttribute('target', this.iframe.name);
      this.iframe.addEventListener('load', this.onIframeLoad);
    }

    this.form?.addEventListener('submit', this.onSubmit);

    if (this.form && this.successMessage && !this.successMessage.hidden) {
      this.form.classList.add('is-success');
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.form?.removeEventListener('submit', this.onSubmit);
    this.iframe?.removeEventListener('load', this.onIframeLoad);
  }

  /**
   * @param {SubmitEvent} event
   */
  onSubmit(event) {
    if (this.form?.classList.contains('is-success')) {
      event.preventDefault();
      return;
    }

    if (this.errorMessage) this.errorMessage.hidden = true;
    this.pendingSubmit = true;
  }

  onIframeLoad() {
    if (!this.pendingSubmit || !this.form) return;
    this.pendingSubmit = false;

    try {
      const doc = this.iframe?.contentDocument;
      const iframeError = doc?.querySelector('.email-signup__message--error:not([hidden])');
      if (iframeError && !doc.querySelector('.email-signup__message--success:not([hidden])')) {
        const message = iframeError.textContent?.trim();
        if (this.errorMessage) {
          const text = this.errorMessage.querySelector('.email-signup__message-text');
          if (text && message) text.textContent = message;
          this.errorMessage.hidden = false;
          this.errorMessage.focus();
        }
        return;
      }
    } catch {
      // Cross-origin iframe; treat as success after a native post.
    }

    this.showSuccess(this.form);
  }

  /**
   * @param {HTMLFormElement} form
   */
  showSuccess(form) {
    form.classList.add('is-success');
    if (this.input instanceof HTMLInputElement) {
      this.input.value = '';
    }
    if (this.successMessage) {
      this.successMessage.hidden = false;
      this.successMessage.focus();
    }
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
